import {NavigationLinkOptions} from './types';
import {DrawerProps as DProps} from '../Drawer';
import {FC, MouseEventHandler, useState} from 'react';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import {Box} from '../Box';
import {IconButton} from '../Button';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import {Typography} from '../Typography';
import {NavItem} from './NavItem';
import {styled} from '@mui/material/styles';
import {navCssClassNames} from './navCssClassNames';
import clsx from 'clsx';
import {StyledNavDrawer} from './base/StyledNavDrawer';
import {StyledNavItemButton} from './base/StyledNavItemButton';
import {StyledDrawerNav} from './base/StyledDrawerNav';
import {StackedNavList} from './base/StackedNavList';

export interface NavItemDrawerProps extends
    NavigationLinkOptions,
    Pick<DProps, 'onClose' | 'position' | 'container'> {}

const StyledMenuHeader = styled('div')(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.tokens.FuelSpacing2,
    paddingBottom: theme.tokens.FuelSpacing2,
    paddingLeft: theme.tokens.FuelSpacing4,
    paddingRight: '3.25rem', // iconSize + padding(1rem)
    borderBottom: `solid 1px ${theme.tokens.FuelColorBorderLight}`,
    textAlign: 'center',

    '& .FuelStyledMenuHeader-text': {
        paddingLeft: theme.tokens.FuelSpacing2,
        paddingRight: theme.tokens.FuelSpacing2
    }
}));

export const NavItemDrawer: FC<NavItemDrawerProps> = ({
    label,
    selected,
    position,
    subNavigationItems: items,
    onClose,
    onClick,
    container,
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleNavItemClick: MouseEventHandler<HTMLButtonElement> = (event) => {
        onClick?.(event);
        setIsOpen(true);
    };

    const handleIconButtonClick: MouseEventHandler<HTMLButtonElement> = () => {
        setIsOpen(false);
    };

    const recursiveSubDrawerProps: DProps = {
        onClose,
        position,
        variant: 'persistent'
    };

    return (
        <>
            <StyledNavItemButton
                className={navCssClassNames.itemDrawerButton}
                endIcon={<KeyboardArrowRight/>}
                onClick={handleNavItemClick}
                orientation='side'
                selected={selected}
                {...props}
            >
                {label}
            </StyledNavItemButton>
            <StyledNavDrawer
                {...recursiveSubDrawerProps}
                open={isOpen}
            >
                <Box
                    className={navCssClassNames.drawerBox}
                    display='flex'
                    flexDirection='column'
                >
                    <StyledMenuHeader className={navCssClassNames.drawerHeader}>
                        <IconButton onClick={handleIconButtonClick}>
                            <KeyboardArrowLeft/>
                        </IconButton>
                        <Typography
                            className={clsx(
                                navCssClassNames.drawerHeaderText,
                                'FuelStyledMenuHeader-text'
                            )}
                            color='secondary'
                            component='span'
                            flexGrow={1}
                            noWrap
                            textAlign='center'
                            variant='h4'
                        >
                            {label}
                        </Typography>
                    </StyledMenuHeader>
                    <StyledDrawerNav className={navCssClassNames.drawerNav}>
                        <StackedNavList className={navCssClassNames.drawerList}>
                            {items && items.map((item, itemIndex) => {
                                if (item.subNavigationItems && item.subNavigationItems.length > 0) {
                                    return (
                                        <NavItem key={`${item.label}-${itemIndex}`}>
                                            <NavItemDrawer
                                                {...item}
                                                {...recursiveSubDrawerProps}
                                            />
                                        </NavItem>
                                    );
                                }

                                return (
                                    <NavItem key={`${item.label}-${itemIndex}`}>
                                        <StyledNavItemButton
                                            {...item}
                                            onClick={(clickEvent): void => {
                                                item.onClick?.(clickEvent);
                                                onClose?.(clickEvent, 'backdropClick');
                                            }}
                                            orientation='side'
                                        >
                                            {item.label}
                                        </StyledNavItemButton>
                                    </NavItem>
                                );
                            })}
                        </StackedNavList>
                    </StyledDrawerNav>
                </Box>
            </StyledNavDrawer>
        </>
    );
};
